import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import employeeService from 'portal/services/employeeService';

// Super Admin (1), Admin (2) and Reception (3) are the roles the backend lets
// approve or reject an attendance-change request.
// Who decides an attendance change request: Super Admin, Admin, and any role given
// manage_staff_attendance (Reception by default) — what the server checks too.
const canDecideAttendance = (user, permissions) =>
  user?.RoleId === 1 || user?.RoleId === 2 || (permissions || []).includes("manage_staff_attendance");

const NOTIFICATION_CONFIG = {
  appointment: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    tabIcon: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    bgColor: 'bg-blue-50',
    iconColor: 'text-blue-500',
    dotColor: 'bg-blue-500',
  },
  payment: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    tabIcon: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    bgColor: 'bg-green-50',
    iconColor: 'text-green-500',
    dotColor: 'bg-green-500',
  },
  waiting_list: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    tabIcon: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    bgColor: 'bg-orange-50',
    iconColor: 'text-orange-500',
    dotColor: 'bg-orange-500',
  },
  clinical: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    ),
    tabIcon: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
      </svg>
    ),
    bgColor: 'bg-red-50',
    iconColor: 'text-red-500',
    dotColor: 'bg-red-500',
  },
  staff: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    tabIcon: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    bgColor: 'bg-purple-50',
    iconColor: 'text-purple-500',
    dotColor: 'bg-purple-500',
  },
  attendance: {
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15l2 2 4-4" />
      </svg>
    ),
    tabIcon: (
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15l2 2 4-4" />
      </svg>
    ),
    bgColor: 'bg-teal-50',
    iconColor: 'text-teal-600',
    dotColor: 'bg-teal-500',
  },
};

const TABS = [
  { key: 'all', label: 'All', icon: null },
  { key: 'appointment', label: 'Appointments' },
  { key: 'payment', label: 'Payments' },
  { key: 'clinical', label: 'Alerts' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'staff', label: 'Staff' },
];

const STATUS_LABEL = { Yes: 'Present', No: 'Absent', Half: 'Half Day' };

// A pending attendance change: the only place it can be acted on, so it is
// pinned (no dismiss) until an approver approves or cancels it.
const isPendingAttendanceApproval = (n) => n.action_type === 'attendance_change_approval';

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

const NotificationPanel = ({
  notifications,
  smartAlerts,
  filter,
  onChangeFilter,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onRefresh,
  onClose,
  onViewAll,
}) => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const permissions = useSelector((state) => state.auth.permissions);
  const canApproveAttendance = canDecideAttendance(user, permissions);
  const [hoveredId, setHoveredId] = useState(null);
  const [processingId, setProcessingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const handleClick = (notification) => {
    if (!notification.is_read) {
      onMarkAsRead(notification.id);
    }
    // A pending attendance change opens its Approve/Cancel detail in place
    // instead of navigating away — deciding it is the whole point of the card.
    if (isPendingAttendanceApproval(notification)) {
      setExpandedId(prev => (prev === notification.id ? null : notification.id));
      return;
    }
    if (notification.link) {
      navigate(notification.link);
      onClose();
    }
  };

  const handleAction = (e, notification, action) => {
    e.stopPropagation();
    if (action === 'assign_slot' && notification.action_data?.waiting_list_id) {
      navigate('/portal/waiting-list');
      onClose();
    }
  };

  const handleAttendanceDecision = async (e, notification, decision) => {
    e.stopPropagation();
    const requestId = notification.action_data?.requestId;
    if (!requestId) return;
    setProcessingId(notification.id);
    try {
      const res = decision === 'approve'
        ? await employeeService.approveAttendanceRequest(requestId)
        : await employeeService.rejectAttendanceRequest(requestId);
      // Backend always responds HTTP 200 for this route, even on a denied/failed
      // action — rescode is the real success/failure signal, not the HTTP status.
      if (res?.rescode === 1) {
        toast.success(res?.message || (decision === 'approve' ? 'Attendance change approved.' : 'Attendance change rejected.'));
        // The backend rewrites the card into a plain outcome notice for every
        // approver, so re-pull rather than dropping it locally.
        if (onRefresh) await onRefresh();
      } else {
        toast.error(res?.message || 'Failed to process attendance change.');
        // Most often "already resolved" — this card is stale, so re-read rather
        // than leaving a button on screen that can only fail again.
        if (onRefresh) await onRefresh();
      }
    } catch (err) {
      toast.error(err?.message || 'Failed to process attendance change.');
    } finally {
      setProcessingId(null);
    }
  };

  // Critical + high alerts pinned at top
  const pinnedAlerts = smartAlerts.filter(a => a.priority === 'critical' || a.priority === 'high');

  return (
    <div
      className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-[64px] sm:top-auto sm:mt-2 z-20 bg-white rounded-xl shadow-xl ring-1 ring-black/5 w-auto sm:w-[400px] flex flex-col"
      style={{ maxHeight: 'min(540px, calc(100dvh - 96px))' }}
    >

      {/* ── Header ── */}
      <div className="flex items-center justify-between px-4 py-3">
        <h3 className="text-[14px] font-semibold text-gray-800">Notifications</h3>
        <button
          onClick={onMarkAllAsRead}
          className="flex items-center gap-1 text-xs text-brand-500 hover:text-brand-600 font-medium transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          Mark all as read
        </button>
      </div>

      {/* ── Pinned Critical Alerts ── */}
      {pinnedAlerts.length > 0 && (
        <div className="mx-3 mb-2 rounded-lg bg-red-50 border border-red-100 overflow-hidden">
          {pinnedAlerts.map((alert, i) => (
            <div
              key={i}
              className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer hover:bg-red-100/50 transition-colors ${i > 0 ? 'border-t border-red-100' : ''}`}
              onClick={() => {
                if (alert.link) { navigate(alert.link); onClose(); }
              }}
            >
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-red-700">{alert.title}</p>
                <p className="text-[11px] text-red-600 mt-0.5">{alert.description}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); if (alert.link) { navigate(alert.link); onClose(); } }}
                className="flex-shrink-0 px-2 py-1 text-[10px] font-semibold text-red-600 bg-red-100 rounded-md hover:bg-red-200 transition-colors whitespace-nowrap mt-0.5"
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter Tabs ── */}
      <div className="flex gap-1.5 px-3 py-2.5 border-t border-b border-gray-100 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        {TABS.map(tab => {
          const isActive = filter === tab.key;
          const tabConfig = NOTIFICATION_CONFIG[tab.key];
          return (
            <button
              key={tab.key}
              onClick={() => onChangeFilter(tab.key)}
              className={`flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium rounded-full whitespace-nowrap flex-shrink-0 transition-all ${
                isActive
                  ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-200'
                  : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700'
              }`}
            >
              {tabConfig && (
                <span className={isActive ? 'text-indigo-500' : 'text-gray-400'}>
                  {tabConfig.tabIcon}
                </span>
              )}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* ── Notification List ── */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: '340px', scrollBehavior: 'smooth' }}>
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <svg className="w-10 h-10 mb-2 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="text-sm font-medium text-gray-400">No notifications</p>
            <p className="text-xs text-gray-300 mt-0.5">You're all caught up!</p>
          </div>
        ) : (
          notifications.map(notification => {
            const config = NOTIFICATION_CONFIG[notification.type] || NOTIFICATION_CONFIG.appointment;
            const isHovered = hoveredId === notification.id;
            return (
              <div
                key={notification.id}
                onClick={() => handleClick(notification)}
                onMouseEnter={() => setHoveredId(notification.id)}
                onMouseLeave={() => setHoveredId(null)}
                className={`relative flex items-start gap-3 px-4 py-3 cursor-pointer border-b border-gray-50 transition-colors ${
                  notification.is_read ? 'bg-white hover:bg-gray-50' : 'bg-indigo-50/20 hover:bg-indigo-50/40'
                }`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${config.bgColor} ${config.iconColor} mt-0.5`}>
                  {config.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pr-5">
                  <div className="flex items-center gap-1.5">
                    <p className={`text-[13px] font-semibold leading-tight ${notification.is_read ? 'text-gray-600' : 'text-gray-900'}`}>
                      {notification.title}
                    </p>
                    {!notification.is_read && (
                      <span className={`flex-shrink-0 w-2 h-2 rounded-full ${config.dotColor}`} />
                    )}
                  </div>
                  <p className={`text-[12px] mt-0.5 line-clamp-2 ${notification.is_read ? 'text-gray-400' : 'text-gray-500'}`}>
                    {notification.description}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {timeAgo(notification.created_at)}
                  </p>

                  {/* Action Buttons */}
                  {notification.action_type === 'assign_slot' && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={(e) => handleAction(e, notification, 'assign_slot')}
                        className="px-3 py-1 text-[11px] font-semibold text-white bg-brand-500 rounded-md hover:bg-brand-600 transition-colors"
                      >
                        Assign Slot
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
                        className="px-3 py-1 text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                  {isPendingAttendanceApproval(notification) && (
                    canApproveAttendance ? (
                      <>
                        {expandedId === notification.id && notification.action_data && (
                          <div className="mt-2 rounded-md bg-teal-50/70 px-2.5 py-2 text-[11px] text-teal-800 space-y-0.5">
                            {notification.action_data.staffName && (
                              <p><span className="text-teal-600">Staff:</span> {notification.action_data.staffName}</p>
                            )}
                            {notification.action_data.date && (
                              <p><span className="text-teal-600">Date:</span> {notification.action_data.date}</p>
                            )}
                            <p>
                              <span className="text-teal-600">Change:</span>{' '}
                              {STATUS_LABEL[notification.action_data.previousStatus] || notification.action_data.previousStatus}
                              {' \u2192 '}
                              {STATUS_LABEL[notification.action_data.requestedStatus] || notification.action_data.requestedStatus}
                            </p>
                          </div>
                        )}
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={(e) => handleAttendanceDecision(e, notification, 'approve')}
                            disabled={processingId === notification.id}
                            className="px-3 py-1 text-[11px] font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={(e) => handleAttendanceDecision(e, notification, 'reject')}
                            disabled={processingId === notification.id}
                            className="px-3 py-1 text-[11px] font-semibold text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <span className="inline-block mt-2 px-2 py-0.5 text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full">
                        Awaiting Admin approval
                      </span>
                    )
                  )}
                </div>

                {/* Hover Actions */}
                <div className={`absolute right-3 top-3 flex items-center gap-0.5 transition-opacity ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
                  {!notification.is_read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onMarkAsRead(notification.id); }}
                      className="p-1 rounded-md text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                      title="Mark as read"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                  )}
                  {!isPendingAttendanceApproval(notification) && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
                    className="p-1 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Delete"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* ── Footer ── */}
      <div className="border-t border-gray-100">
        <button
          onClick={onViewAll}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-[13px] font-medium text-brand-500 hover:bg-gray-50 transition-colors"
        >
          View All Notifications
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificationPanel;
